import React, { useState, useRef, useEffect } from 'react';

import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, MenuItem, Select, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { useSnackbar } from 'notistack';

import { TYPE_OPTIONS_ENUM, MODALITY_OPTIONS } from '../../constants/observations';
import { useAppContext } from '../../contexts/AppContext';
import QualityObservationService from '../../services/quality-observation.service';
import UserService from '../../services/user.service';

const useStyles = makeStyles((theme) => ({
  buttonsContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    width: '100%',
  },
  cancelButton: {
    border: '1px solid #e0e0e0',
    padding: theme.spacing(1.5),
    width: '100%',
  },
  closeButton: {
    color: theme.palette.grey[500],
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  dialogActions: {
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  dialogContent: {
    padding: theme.spacing(3),
  },
  dialogPaper: {
    maxWidth: '500px',
    width: '100%',
  },
  dialogTitle: {
    margin: 0,
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  fileNameText: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    marginLeft: theme.spacing(1),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  formControl: {
    marginBottom: theme.spacing(3),
    width: '100%',
  },
  instructionText: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    textAlign: 'center',
  },
  submitButton: {
    '&:hover': {
      backgroundColor: '#3d8bd1',
    },
    backgroundColor: '#4dabf5',
    color: 'white',
    padding: theme.spacing(1.5),
    width: '100%',
  },
  uploadButton: {
    '&:hover': {
      backgroundColor: '#3d8bd1',
    },
    backgroundColor: '#4dabf5',
    color: 'white',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    width: '100%',
  },
  uploadContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(1),
  },
}));

const initialFormData = {
  examName: ``,
  modality: '',
  patientName: '',
  technicianId: '',
  type: 'routine',
};

const CreateQualityObservationModal = ({ open, onClose }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { workspaceId } = useAppContext();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [videoFileName, setVideoFileName] = useState('');
  const [extractedAudio, setExtractedAudio] = useState(null);
  const [extractingAudio, setExtractingAudio] = useState(false);

  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        setLoadingUsers(true);
        const userService = new UserService();
        const response = await userService.getUsers({
          limit: 100,
          status: 'Active',
        });

        const sortedUsers = response?.data?.sort((a, b) => a.name.localeCompare(b.name)) || [];

        setUsers(sortedUsers);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log('Error fetching technicians:', error);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchTechnicians();
  }, [workspaceId]);

  const extractAudioFromVideo = async (videoFile) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();

      video.src = URL.createObjectURL(videoFile);
      video.crossOrigin = 'anonymous';

      video.addEventListener('loadedmetadata', async () => {
        try {
          const source = audioContext.createMediaElementSource(video);
          const destination = audioContext.createMediaStreamDestination();
          source.connect(destination);

          const mediaRecorder = new MediaRecorder(destination.stream, {
            mimeType: 'audio/webm;codecs=opus',
          });

          const chunks = [];
          mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
              chunks.push(event.data);
            }
          };

          mediaRecorder.onstop = () => {
            const audioBlob = new Blob(chunks, { type: 'audio/webm' });
            const audioFile = new File([audioBlob], `${videoFile.name.split('.')[0]}_audio.webm`, {
              type: 'audio/webm',
            });
            resolve(audioFile);
          };

          mediaRecorder.start();
          video.play();

          video.addEventListener('ended', () => {
            mediaRecorder.stop();
            URL.revokeObjectURL(video.src);
          });
        } catch (error) {
          reject(error);
        }
      });

      video.addEventListener('error', reject);
    });
  };

  const handleVideoFileChange = async (e) => {
    const file = e.target.files[0];

    if (file) {
      const acceptedFormats = ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/webm'];

      if (acceptedFormats.includes(file.type) || file.name.toLowerCase().match(/\.(mp4|avi|mov|wmv|webm)$/)) {
        setVideoFile(file);
        setVideoFileName(file.name);

        try {
          setExtractingAudio(true);
          const audioFile = await extractAudioFromVideo(file);
          setExtractedAudio(audioFile);
        } catch (error) {
          enqueueSnackbar('Failed to extract audio:', { variant: 'error' });
        } finally {
          setExtractingAudio(false);
        }
      } else {
        enqueueSnackbar('Please upload a video file in MP4, AVI, MOV, or WMV format', { variant: 'error' });
        e.target.value = '';
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleSubmit = async () => {
    try {
      if (!videoFile) {
        enqueueSnackbar('Please upload a video file', { variant: 'error' });
        return;
      }

      if (!extractedAudio && formData.type === 'routine') {
        enqueueSnackbar('No audio extracted - demo evaluation will be created', { variant: 'warning' });
      }

      setLoading(true);

      const formDataToSubmit = new FormData();
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== null && formData[key] !== undefined) {
          formDataToSubmit.append(key, formData[key]);
        }
      });

      formDataToSubmit.append('workspaceId', workspaceId);
      formDataToSubmit.append('video', videoFile);

      if (extractedAudio) {
        formDataToSubmit.append('audio', extractedAudio);
      }

      await QualityObservationService.create(formDataToSubmit);

      enqueueSnackbar('Quality observation created successfully', { variant: 'success' });
      handleClose();
    } catch (error) {
      enqueueSnackbar('Failed to create quality observation', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setVideoFile(null);
    setVideoFileName('');
    setExtractedAudio(null);
    onClose();
  };

  const selectMenuProps = {
    anchorOrigin: {
      horizontal: 'left',
      vertical: 'bottom',
    },
    getContentAnchorEl: null,
    transformOrigin: {
      horizontal: 'left',
      vertical: 'top',
    },
  };

  return (
    <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title" classes={{ paper: classes.dialogPaper }}>
      <DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
        Create Quality Observation
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Typography variant="body2" className={classes.instructionText}>
          Please fill in the details to create a new quality observation
        </Typography>

        <FormControl variant="outlined" className={classes.formControl}>
          <Select id="type" name="type" value={formData.type} onChange={handleChange} required fullWidth displayEmpty MenuProps={selectMenuProps}>
            <MenuItem value="" disabled>
              <em>Select Type</em>
            </MenuItem>
            <MenuItem value={TYPE_OPTIONS_ENUM.STAT}>STAT</MenuItem>
            <MenuItem value={TYPE_OPTIONS_ENUM.CALL_BACK}>Call Back</MenuItem>
            <MenuItem value={TYPE_OPTIONS_ENUM.ROUTINE}>Routine</MenuItem>
          </Select>
        </FormControl>

        <FormControl variant="outlined" className={classes.formControl}>
          <Select id="modality" name="modality" value={formData.modality} onChange={handleChange} required fullWidth displayEmpty MenuProps={selectMenuProps}>
            <MenuItem value="" disabled>
              <em>Select Modality</em>
            </MenuItem>
            {MODALITY_OPTIONS.map((modality) => (
              <MenuItem key={modality.value} value={modality.value}>
                {modality.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          variant="outlined"
          required
          fullWidth
          id="patientName"
          placeholder="Patient Name"
          name="patientName"
          value={formData.patientName}
          onChange={handleChange}
          className={classes.formControl}
        />

        <FormControl variant="outlined" className={classes.formControl}>
          <Select
            id="technicianId"
            name="technicianId"
            value={formData.technicianId}
            onChange={handleChange}
            fullWidth
            disabled={loadingUsers}
            displayEmpty
            MenuProps={selectMenuProps}>
            <MenuItem value="">
              <em>{loadingUsers ? 'Loading technicians...' : 'Select Technician'}</em>
            </MenuItem>
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name} ({user.email})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          variant="outlined"
          required
          fullWidth
          id="examName"
          placeholder="Exam Name"
          name="examName"
          value={formData.examName}
          onChange={handleChange}
          className={classes.formControl}
        />

        <input
          accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
          style={{ display: 'none' }}
          id="video-file-upload"
          type="file"
          onChange={handleVideoFileChange}
          ref={fileInputRef}
        />

        <div className={classes.uploadContainer}>
          <Button
            variant="contained"
            className={classes.uploadButton}
            disabled={extractingAudio}
            loading={extractingAudio}
            startIcon={<CloudUploadIcon />}
            onClick={handleUploadClick}>
            {videoFileName ? `${extractingAudio ? 'Processing Video' : 'Change Video'}` : 'Upload Video'}
          </Button>

          {videoFileName && (
            <Typography variant="body2" className={classes.fileNameText}>
              <span>{videoFileName}</span> {extractingAudio && <CircularProgress color="primary" className="ml-2" size={20} />}
            </Typography>
          )}
        </div>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <div className={classes.buttonsContainer}>
          <Button onClick={handleClose} className={classes.cancelButton}>
            Close
          </Button>

          <Button
            onClick={handleSubmit}
            variant="contained"
            className={classes.submitButton}
            disabled={loading || extractingAudio || !formData.type || !formData.modality || !formData.patientName || !formData.examName || !videoFile}>
            {loading ? 'Creating...' : 'Create Observation'}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default CreateQualityObservationModal;
